import React, { useState, useEffect } from 'react';
import { Sidebar } from './sidebar';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { LogOut } from 'lucide-react';
import { ActionInboxBell } from './action-inbox-bell';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/context/auth-context';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getInitials } from '@/lib/utils';
import { SiteSelector } from '@/components/site/site-selector';
import { LanguageSelector } from '@/components/language/LanguageSelector';
import { authFetch } from '@/utils/auth-fetch';
import { NavigationSearch } from './navigation-search';
import { DidYouKnowBanner } from './did-you-know-banner';

interface PageLayoutProps {
  children: React.ReactNode;
}

interface UserPreferences {
  selectedSiteId?: string | null;
  onboardingCollapsed?: Record<string, boolean>;
  sidebarCollapsed?: boolean;
}

export const PageLayout: React.FC<PageLayoutProps> = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const { user } = useAuth();

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await authFetch('/users/me/preferences');
        if (response.ok) {
          const data = await response.json();
          // Set sidebar collapsed state from preferences
          if (data.sidebarCollapsed !== undefined) {
            setIsSidebarCollapsed(data.sidebarCollapsed);
          }
          setPreferencesLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load user preferences:', error);
        setPreferencesLoaded(true); // Still mark as loaded to show UI
      }
    };

    if (user) {
      loadPreferences();
    }
  }, [user]);

  // Save sidebar collapsed state to preferences
  const savePreferences = async (collapsed: boolean) => {
    try {
      await authFetch('/users/me/preferences', {
        method: 'PUT',
        body: JSON.stringify({ sidebarCollapsed: collapsed })
      });
    } catch (error) {
      console.error('Failed to save sidebar preference:', error);
    }
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newCollapsedState);
    // Save preference to backend
    savePreferences(newCollapsedState);
  };

  return (
    <div className="flex h-screen w-full bg-background dark:bg-background">
      <Sidebar collapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
      <div className="flex flex-col flex-1 overflow-hidden">
        <header className="h-16 border-b border-border flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button onClick={toggleSidebar} className="p-2 rounded-md hover:bg-secondary">
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 15 15" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="text-foreground"
              >
                <path
                  d="M1.5 3C1.22386 3 1 3.22386 1 3.5C1 3.77614 1.22386 4 1.5 4H13.5C13.7761 4 14 3.77614 14 3.5C14 3.22386 13.7761 3 13.5 3H1.5ZM1.5 7C1.22386 7 1 7.22386 1 7.5C1 7.77614 1.22386 8 1.5 8H13.5C13.7761 8 14 7.77614 14 7.5C14 7.22386 13.7761 7 13.5 7H1.5ZM1.5 11C1.22386 11 1 11.2239 1 11.5C1 11.7761 1.22386 12 1.5 12H13.5C13.7761 12 14 11.7761 14 11.5C14 11.2239 13.7761 11 13.5 11H1.5Z"
                  fill="currentColor"
                  fillRule="evenodd"
                  clipRule="evenodd"
                ></path>
              </svg>
            </button>
            <NavigationSearch />
          </div>
          <div className="flex items-center gap-4">
            <SiteSelector />
            <LanguageSelector />
            <div className="h-8 w-px bg-border" />
            <ActionInboxBell />
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    {user?.image && <AvatarImage src={user.image} alt={user.name || 'User'} />}
                    <AvatarFallback>{user?.name ? getInitials(user.name) : 'U'}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name || 'User'}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || ''}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer text-destructive focus:text-destructive" asChild>
                  <Link to="/logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        
        {/*
          ⛔ `relative` IS LOAD-BEARING, NOT COSMETIC — it is the fix for the CEO's blank page.
          Tailwind's `sr-only` is `position: absolute`. With NO positioned ancestor its containing
          block is the INITIAL CONTAINING BLOCK, so it is not clipped by this scroller and is not
          contained by <body>: it is laid out at its static position deep inside the scrolled content
          and extends <html>'s scrollable height far past the viewport. Clicking a <Label htmlFor>
          focuses that hidden input, the browser scrolls a focused element into view, and THE WINDOW
          LANDS IN THE EMPTY REGION BELOW THE SHELL — a white screen over a perfectly healthy DOM.
          Measured: html 3263 / body 776 on admin-preprod; adding this took html to 776.
          ⇒ Positioning the SCROLLER (rather than the 13 sr-only call sites) contains every
            absolutely-positioned descendant, present and future. See PR for the escape-surface audit.
        */}
        <div className="flex-1 overflow-auto relative">
          <div className="max-w-screen-2xl mx-auto p-6">
            <DidYouKnowBanner />
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};